import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { CompanyFindManyQueryInterface } from '@zaoblako/shared/modules/client/dtos/company/company-find-many-query.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { MultiSelectFilterEvent } from 'primeng/multiselect';
import { uniqBy } from 'lodash';
import { map } from 'rxjs/operators';

@Component({
  selector: 'crm-company-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SafeHtmlPipe],
  templateUrl: './company-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyMultiSelectComponent implements OnInit, OnDestroy {
  private static readonly ENTITY_CACHE_KIND = 'company';

  @ViewChild('multiSelectComponent') public multiSelectComponent: MultiSelectComponent;

  private readonly LIMIT = 20;
  private readonly dropdownSelect: CompanyFindManyQueryInterface['select'] = ['id', 'name', 'deleted'];

  @Input() public companies: CompanyFindInterface[] = [];

  @Input() public selectedCompanies: CompanyFindInterface[] = [];
  @Output() public selectedCompaniesChange = new EventEmitter<CompanyFindInterface[]>();

  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public label = 'Компания';
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;
  @Input() public itemStyleClass: string;
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  private lazyLoading = false;
  private maxCount = 0;
  private offset = 0;
  private search = '';
  private pendingSelectedIds: number[] | null = null;

  public get selectionLoading(): boolean {
    return !!this.pendingSelectedIds?.length;
  }

  @Input() public set setSelectedCompanyIds(ids: number[]) {
    if (!ids?.length) {
      return;
    }

    const normalizedIds = ids.map((id) => (typeof id === 'string' ? +id : id));

    if (
      this.selectedCompanies?.length &&
      this.selectedCompanies.every((selectedCompany) => normalizedIds.includes(selectedCompany.id))
    ) {
      return;
    }

    if (this.companies.length) {
      const localMissing = normalizedIds.filter((id) => !this.companies.some((company) => company.id === id));
      if (!localMissing.length) {
        this.selectedCompanies = this.companies.filter((company) => normalizedIds.includes(company.id));
        this.entityCache.putEntities(CompanyMultiSelectComponent.ENTITY_CACHE_KIND, this.selectedCompanies);
        this.cdRef.markForCheck();
        return;
      }
      this.entityCache.putEntities(CompanyMultiSelectComponent.ENTITY_CACHE_KIND, this.companies);
    }

    this.pendingSelectedIds = normalizedIds;
    this.loadSelectedCompaniesByIds(normalizedIds);
  }

  public constructor(
    private readonly companyService: CompanyService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly globalService: GlobalService,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public onSelectedCompaniesChange(companies: CompanyFindInterface[]): void {
    this.entityCache.putEntities(CompanyMultiSelectComponent.ENTITY_CACHE_KIND, companies ?? []);
    this.selectedCompaniesChange.emit(companies);
  }

  public ngOnInit(): void {
    if (this.shouldLoadFromApi()) {
      this.firstLoadCompanies();
    }
  }

  public ngOnDestroy(): void {
    void 0;
  }

  public loadMoreCompanies(event: { first: number; last: number }): void {
    if (
      !this.shouldLoadFromApi() ||
      this.offset > event.last ||
      this.maxCount <= this.companies.length ||
      this.lazyLoading
    ) {
      return;
    }

    this.lazyLoading = true;
    this.companyService.findMany(this.getQueryParams()).subscribe({
      next: (page) => {
        this.offset += this.LIMIT;
        this.companies = uniqBy([...this.companies, ...page.items], 'id');
        this.maxCount = page.count;
        this.applyPendingSelectedIds();
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.lazyLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public filterCompanies(event: MultiSelectFilterEvent): void {
    if (!this.shouldLoadFromApi()) {
      return;
    }

    this.search = event.filter ?? '';
    this.offset = 0;

    if (this.search.length) {
      this.lazyLoading = true;
      this.companyService.findMany(this.getQueryParams()).subscribe({
        next: (page) => {
          this.companies = uniqBy([...this.selectedCompanies, ...page.items], 'id');
          this.maxCount = page.count;
          this.offset = this.LIMIT;
          this.applyPendingSelectedIds();
          this.cdRef.markForCheck();
        },
        complete: () => {
          this.lazyLoading = false;
          this.cdRef.markForCheck();
        }
      });
    } else {
      this.firstLoadCompanies();
    }
  }

  public toCompany(company: CompanyFindInterface): CompanyFindInterface {
    return company;
  }

  public highlightMatchText(name: string | undefined): string {
    return (
      HighlightMatchTextHelper.highlightMatchText(name, this.multiSelectComponent?.multiSelect?.filterValue) ||
      'Неизвестно'
    );
  }

  private shouldLoadFromApi(): boolean {
    return (
      !this.customRequest &&
      !!this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
    );
  }

  private getQueryParams(): CompanyFindManyQueryInterface {
    const queryParams: CompanyFindManyQueryInterface = {
      limit: this.LIMIT,
      offset: this.offset,
      select: this.dropdownSelect
    };

    if (this.search?.length) {
      queryParams.search = this.search;
    }

    return queryParams;
  }

  private firstLoadCompanies(): void {
    this.offset = 0;
    const fingerprint = this.entityCache.fingerprint({
      limit: this.LIMIT,
      select: this.dropdownSelect
    });

    this.entityCache
      .loadFirstPage(CompanyMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
        this.companyService.findMany(this.getQueryParams())
      )
      .subscribe({
        next: (page) => {
          this.companies = uniqBy([...this.companies, ...page.items], 'id');
          this.maxCount = page.count;
          this.offset = this.LIMIT;
          this.applyPendingSelectedIds();
          this.cdRef.markForCheck();
        }
      });
  }

  private loadSelectedCompaniesByIds(ids: number[]): void {
    this.entityCache
      .loadByIds(CompanyMultiSelectComponent.ENTITY_CACHE_KIND, ids, (missing) =>
        this.companyService
          .findMany({
            includeIds: missing as number[],
            select: this.dropdownSelect,
            limit: missing.length
          })
          .pipe(map(({ items }) => items))
      )
      .subscribe({
        next: (items) => {
          this.companies = uniqBy([...items, ...this.companies], 'id');
          this.selectedCompanies = this.companies.filter((company) => ids.includes(company.id));
          this.pendingSelectedIds = null;
          if (this.selectedCompanies.length !== ids.length) {
            this.onSelectedCompaniesChange(this.selectedCompanies);
          }
          this.cdRef.markForCheck();
        },
        error: () => {
          this.pendingSelectedIds = null;
          this.selectedCompanies = [];
          this.onSelectedCompaniesChange([]);
          this.cdRef.markForCheck();
        }
      });
  }

  private applyPendingSelectedIds(): void {
    if (!this.pendingSelectedIds?.length) {
      return;
    }

    const requestedIds = this.pendingSelectedIds;
    this.selectedCompanies = this.companies.filter((company) => requestedIds.includes(company.id));
    if (this.selectedCompanies.length === requestedIds.length) {
      this.pendingSelectedIds = null;
    }
  }
}
